<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { CanvasMetaExt } from '@vp/core';
import { canvasOpens } from '../canvas-use';
import { locale, tn, t } from '../i18n';
import { ui } from '../editor/bridge';
import { profileCard } from '../profile-card';
import { snapStatBox, type StatGuide } from '../profile-guides';
import {
  PROFILE_BOARD_MAX_H,
  STAT_MIN_H,
  STAT_MIN_W,
  cardPixels,
  defaultProfileLayout,
  hiddenPanels,
  hideCard,
  markLayoutPending,
  moveCard,
  profilePanels,
  readProfileLayout,
  showCard,
  withCoreCards,
  writeProfileLayout,
  type ProfileLayout,
  type ProfilePanel,
} from '../profile-panels';
import { addSticker, MAX_STICKERS, moveSticker, readStickers, removeSticker, STICKER_SET, writeStickers, type StickerSheet } from '../profile-stickers';
import { useApp } from '../stores/app';
import AppNav from '../components/AppNav.vue';
import Icon from '../components/Icon.vue';
import ProfileCardFrame from '../components/ProfileCardFrame.vue';
import { cardEditor, initUserCards, removeUserCard, userCards } from '../user-cards';
import { ask } from '../dialogs';
import CardBackdrop from '../components/CardBackdrop.vue';
import LookEditor from '../components/LookEditor.vue';
import type { CardLook } from '@vp/shared-types';
import { cardLook, profileLooks, setPersonLook } from '../profile-looks';
import { frameCss } from '../card-look';
import { showRequests } from '../profile-panels';
import { moduleFeedTools, modulesState, profileCardHtml, runCommand } from '../modules';
import { currentProfileId } from '../profiles/state';
import { getProfile } from '../profiles/registry';
import { settings } from '../platform';
import { reduceEffects } from '../theme';

/** Profile: how many canvases and objects there are, and the canvas opened most often. */
const app = useApp();

const canvasCount = computed(() => app.canvases.length);
const objectCount = computed(() => app.canvases.reduce((sum, c) => sum + c.objectCount, 0));

initUserCards();

/** The platform's card editor: a new card, or one of the person's. */
function openCardEditor(id: string | null): void {
  cardEditor.id = id;
  app.go('cards');
}
async function deleteUserCard(id: string): Promise<void> {
  if (await ask(t('cardEditor.removeAsk'), { danger: true, okLabel: t('cardEditor.remove') })) removeUserCard(id);
}

/** API 4.10: the profile's counters a module card may show (`data-vp-live="profile:…"`, permission `profile.stats`). */
const createdAt = ref<number | null>(null);
void (async () => {
  const id = currentProfileId();
  createdAt.value = id ? ((await getProfile(id))?.createdAt ?? null) : null;
})();
const cardStats = computed(() => ({
  canvases: canvasCount.value,
  objects: objectCount.value,
  opens: Object.values(canvasOpens()).reduce((a, b) => a + b, 0),
  days: createdAt.value ? Math.max(1, Math.ceil((Date.now() - createdAt.value) / 86_400_000)) : 1,
}));

/** API 4.10: the look of a card — the module's with the person's over it — and of the tray. */
function lookOf(id: string): CardLook {
  return cardLook(id, profilePanels().find((p) => p.id === id)?.look);
}
const trayLook = computed<CardLook>(() => profileLooks().tray ?? {});
/** The card (or `tray`) whose look is being edited. */
const lookFor = ref<string | null>(null);
const lookTitle = computed(() => {
  if (lookFor.value === 'tray') return t('look.tray');
  const panel = profilePanels().find((p) => p.id === lookFor.value);
  return panel ? panelTitle(panel) : '';
});
function editedLook(): CardLook {
  return lookFor.value === 'tray' ? trayLook.value : lookFor.value ? lookOf(lookFor.value) : {};
}
function changeLook(look: CardLook): void {
  if (lookFor.value) setPersonLook(lookFor.value === 'tray' ? null : lookFor.value, look);
}
function resetLook(): void {
  if (lookFor.value) setPersonLook(lookFor.value === 'tray' ? null : lookFor.value, null);
}
/** Modules that make cards (`profile.cards`): their feed tools open their editors. */
const cardMakers = computed(() => moduleFeedTools(new Set()).filter((f) => modulesState.list.find((m) => m.id === f.moduleId)?.granted.includes('profile.cards')));

/** The edge under the pointer recedes; the opposite edge comes forward. Letters stay on the card. */
const namesEl = ref<HTMLElement | null>(null);
const NAME_TILT_X = 2;
const NAME_TILT_Y = 1.75;
const tiltTarget = { live: false, rx: 0, ry: 0, gx: 50, gy: 50 };
const tiltNow = { rx: 0, ry: 0, gx: 50, gy: 50 };
let tiltFrame = 0;
let tiltStamp = 0;

function paintTilt(live: boolean): void {
  const el = namesEl.value;
  if (!el) return;
  el.style.setProperty('--rx', tiltNow.rx.toFixed(2));
  el.style.setProperty('--ry', tiltNow.ry.toFixed(2));
  el.style.setProperty('--gx', `${tiltNow.gx.toFixed(1)}%`);
  el.style.setProperty('--gy', `${tiltNow.gy.toFixed(1)}%`);
  el.style.setProperty('--edge', Math.min(1, (Math.abs(tiltNow.rx) + Math.abs(tiltNow.ry)) / 8).toFixed(3));
  el.classList.toggle('lit', live);
}

function tiltStep(now: number): void {
  const dt = tiltStamp ? Math.min(40, now - tiltStamp) : 16;
  tiltStamp = now;
  const tau = tiltTarget.live ? 90 : 240;
  const k = 1 - Math.exp(-dt / tau);
  tiltNow.rx += (tiltTarget.rx - tiltNow.rx) * k;
  tiltNow.ry += (tiltTarget.ry - tiltNow.ry) * k;
  tiltNow.gx += (tiltTarget.gx - tiltNow.gx) * k;
  tiltNow.gy += (tiltTarget.gy - tiltNow.gy) * k;
  const left = Math.abs(tiltTarget.rx - tiltNow.rx) + Math.abs(tiltTarget.ry - tiltNow.ry) + Math.abs(tiltTarget.gx - tiltNow.gx) * 0.04 + Math.abs(tiltTarget.gy - tiltNow.gy) * 0.04;
  paintTilt(tiltTarget.live);
  if (left > 0.06) tiltFrame = requestAnimationFrame(tiltStep);
  else {
    tiltFrame = 0;
    tiltStamp = 0;
    tiltNow.rx = tiltTarget.rx;
    tiltNow.ry = tiltTarget.ry;
    tiltNow.gx = tiltTarget.gx;
    tiltNow.gy = tiltTarget.gy;
    paintTilt(tiltTarget.live);
  }
}

function wakeTilt(): void {
  if (!tiltFrame) tiltFrame = requestAnimationFrame(tiltStep);
}

function snapTilt(): void {
  if (tiltFrame) cancelAnimationFrame(tiltFrame);
  tiltFrame = 0;
  tiltStamp = 0;
  tiltTarget.live = false;
  tiltTarget.rx = 0;
  tiltTarget.ry = 0;
  tiltTarget.gx = 50;
  tiltTarget.gy = 50;
  tiltNow.rx = 0;
  tiltNow.ry = 0;
  tiltNow.gx = 50;
  tiltNow.gy = 50;
  paintTilt(false);
}

/** Maps -1..1 so a hover off center already reads as a definite lean. */
function press(unit: number): number {
  const span = Math.min(1, Math.abs(unit));
  return Math.sign(unit) * span ** 0.72;
}

function onNameTilt(e: PointerEvent): void {
  if (reduceEffects() || e.pointerType === 'touch' || editing.value) return;
  const el = e.currentTarget as HTMLElement;
  const r = el.getBoundingClientRect();
  if (r.width < 1 || r.height < 1) return;
  const px = (e.clientX - r.left) / r.width;
  const py = (e.clientY - r.top) / r.height;
  tiltTarget.live = true;
  tiltTarget.rx = press((0.5 - py) * 2) * NAME_TILT_X;
  tiltTarget.ry = press((px - 0.5) * 2) * NAME_TILT_Y;
  tiltTarget.gx = px * 100;
  tiltTarget.gy = py * 100;
  wakeTilt();
}

function endNameTilt(): void {
  if (reduceEffects()) {
    snapTilt();
    return;
  }
  tiltTarget.live = false;
  tiltTarget.rx = 0;
  tiltTarget.ry = 0;
  tiltTarget.gx = 50;
  tiltTarget.gy = 50;
  wakeTilt();
}

watch(
  () => settings.noMotion,
  (off) => {
    if (off) snapTilt();
  },
);

const now = ref(Date.now());
const clockTimer = setInterval(() => {
  now.value = Date.now();
}, 1000);
onBeforeUnmount(() => {
  clearInterval(clockTimer);
  statObserver?.disconnect();
  if (tiltFrame) cancelAnimationFrame(tiltFrame);
});
const CORE_CARDS = new Set(['canvases', 'objects', 'popular', 'clock']);

const editing = ref(false);

/** Stickers on the name card: stuck, moved and peeled off while editing (`profile-stickers.ts`). */
const stickers = ref<StickerSheet>(readStickers());
const stickersFull = computed(() => stickers.value.items.length >= MAX_STICKERS);
const nameBox = ref({ w: 0, h: 0 });
/** Half the drawn emoji. Past this, the sticker crosses the card and folds back over that edge. */
const STICKER_HALF = 18;

function measureNames(): void {
  const el = namesEl.value?.querySelector('.face');
  if (!el) return;
  nameBox.value = { w: el.clientWidth, h: el.clientHeight };
}

/** Fold around the card edge the sticker crosses, so the overhang tucks back instead of sticking out. */
function stickerStyle(s: { x: number; y: number; r: number }): Record<string, string> {
  const { w, h } = nameBox.value;
  let fx = 0;
  let fy = 0;
  let ox = 0;
  let oy = 0;
  if (w > 8 && h > 8) {
    const cx = s.x * w;
    const cy = s.y * h;
    const lean = (hang: number) => Math.min(1, hang / STICKER_HALF);
    const hangR = cx + STICKER_HALF - w;
    const hangL = STICKER_HALF - cx;
    const hangB = cy + STICKER_HALF - h;
    const hangT = STICKER_HALF - cy;
    if (hangR > 0) {
      fx = lean(hangR);
      ox = w - cx;
    } else if (hangL > 0) {
      fx = -lean(hangL);
      ox = -cx;
    }
    if (hangB > 0) {
      fy = -lean(hangB);
      oy = h - cy;
    } else if (hangT > 0) {
      fy = lean(hangT);
      oy = -cy;
    }
  }
  return {
    left: `${s.x * 100}%`,
    top: `${s.y * 100}%`,
    '--r': `${s.r}deg`,
    '--fx': fx.toFixed(3),
    '--fy': fy.toFixed(3),
    '--ox': `calc(50% + ${ox.toFixed(1)}px)`,
    '--oy': `calc(50% + ${oy.toFixed(1)}px)`,
  };
}
let stickerDrag: { id: string; rect: DOMRect; moved: boolean } | null = null;

function stick(emoji: string): void {
  if (stickersFull.value) return;
  stickers.value = writeStickers(addSticker(stickers.value, emoji));
}

function peel(id: string): void {
  stickers.value = writeStickers(removeSticker(stickers.value, id));
}

function startSticker(e: PointerEvent, id: string): void {
  if (!editing.value || e.button !== 0 || !namesEl.value) return;
  if (e.target instanceof Element && e.target.closest('button')) return;
  stickerDrag = { id, rect: namesEl.value.getBoundingClientRect(), moved: false };
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  e.preventDefault();
}

function dragSticker(e: PointerEvent): void {
  if (!stickerDrag) return;
  const { rect, id } = stickerDrag;
  stickerDrag.moved = true;
  stickers.value = moveSticker(stickers.value, id, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
}

function endSticker(): void {
  if (stickerDrag?.moved) stickers.value = writeStickers(stickers.value);
  stickerDrag = null;
}

const libEl = ref<HTMLElement | null>(null);
const libPos = ref<{ x: number; y: number } | null>(null);
let libDrag: { x: number; y: number; left: number; top: number } | null = null;

function startLibDrag(e: PointerEvent): void {
  if (e.button !== 0 || !libEl.value || !libPos.value) return;
  if (e.target instanceof Element && e.target.closest('button')) return;
  libDrag = { x: e.clientX, y: e.clientY, left: libPos.value.x, top: libPos.value.y };
  libEl.value.setPointerCapture(e.pointerId);
}

function moveLibDrag(e: PointerEvent): void {
  if (!libDrag || !libEl.value) return;
  const w = libEl.value.offsetWidth;
  const h = libEl.value.offsetHeight;
  libPos.value = {
    x: Math.min(window.innerWidth - w - 8, Math.max(8, libDrag.left + e.clientX - libDrag.x)),
    y: Math.min(window.innerHeight - h - 8, Math.max(8, libDrag.top + e.clientY - libDrag.y)),
  };
}

function endLibDrag(): void {
  libDrag = null;
}

watch(editing, (on) => {
  if (!on) lookFor.value = null;
  if (!on) return;
  endNameTilt();
  void nextTick(() => {
    const btn = document.querySelector('[data-act="profile-edit"]');
    const el = libEl.value;
    if (!btn || !el || libPos.value) return;
    const r = btn.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    libPos.value = {
      x: Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8)),
      y: Math.min(r.bottom + 8, window.innerHeight - h - 8),
    };
  });
});
const boardEl = ref<HTMLElement | null>(null);
const boardW = ref(760);
/** Null until the person moves, resizes or removes a card. */
const placed = ref<ProfileLayout | null>(readProfileLayout());
const layout = computed(() => withCoreCards(placed.value ?? defaultProfileLayout(Math.max(boardW.value, 1)), Math.max(boardW.value, 1)));
const clockLocale = computed(() => (locale.value === 'ru' ? 'ru-RU' : 'en-US'));
const clockTime = computed(() => new Intl.DateTimeFormat(clockLocale.value, { hour: '2-digit', minute: '2-digit', second: '2-digit' }).format(now.value));
const clockDate = computed(() => new Intl.DateTimeFormat(clockLocale.value, { weekday: 'long', day: 'numeric', month: 'long' }).format(now.value));
const clockStamp = computed(() => {
  const d = new Date(now.value);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
});
const libraryItems = computed(() => hiddenPanels(layout.value));
/** Panels the library knows now: a card of a module that is not running keeps its place but is not drawn. */
const knownIds = computed(() => new Set(profilePanels().map((p) => p.id)));
const shownIds = computed(() => Object.entries(layout.value.cards).filter(([id, card]) => card.shown && knownIds.value.has(id)).map(([id]) => id));
const extraShown = computed(() => profilePanels().filter((panel) => !CORE_CARDS.has(panel.id) && layout.value.cards[panel.id]?.shown));
let statObserver: ResizeObserver | null = null;
let statDrag: { id: string; mode: 'move' | 'resize'; x: number; y: number; box: { x: number; y: number; w: number; h: number } } | null = null;
let dragSnapshot = '';
const statGuides = ref<StatGuide[]>([]);

function isShown(id: string): boolean {
  return layout.value.cards[id]?.shown === true;
}

function panelTitle(panel: ProfilePanel): string {
  if (panel.titleKey) return t(panel.titleKey);
  const title = panel.title;
  if (!title) return panel.id;
  return (locale.value === 'ru' ? title.ru : title.en) || title.en || title.ru || panel.id;
}

const boardH = computed(() => {
  const width = Math.max(boardW.value, 1);
  const bottoms = Object.entries(layout.value.cards)
    .filter(([id, card]) => card.shown && knownIds.value.has(id))
    .map(([, card]) => {
      const box = cardPixels(card, width);
      return box.y + box.h;
    });
  if (!bottoms.length) return STAT_MIN_H;
  return Math.min(PROFILE_BOARD_MAX_H, Math.max(...bottoms));
});

function statStyle(id: string): Record<string, string> {
  const card = layout.value.cards[id];
  if (!card?.shown) return { display: 'none' };
  const box = cardPixels(card, Math.max(boardW.value, 1));
  return { ...frameCss(lookOf(id)), left: `${box.x}px`, top: `${box.y}px`, width: `${box.w}px`, height: `${box.h}px` };
}

function lookBtnStyle(id: string): Record<string, string> {
  const card = layout.value.cards[id];
  if (!card) return {};
  const box = cardPixels(card, Math.max(boardW.value, 1));
  return { left: `${box.x + box.w - 54}px`, top: `${box.y + 6}px` };
}

function editBtnStyle(id: string): Record<string, string> {
  const card = layout.value.cards[id];
  if (!card) return {};
  const box = cardPixels(card, Math.max(boardW.value, 1));
  return { left: `${box.x + box.w - 80}px`, top: `${box.y + 6}px` };
}

function removeStyle(id: string): Record<string, string> {
  const card = layout.value.cards[id];
  if (!card) return {};
  const box = cardPixels(card, Math.max(boardW.value, 1));
  return { left: `${box.x + box.w - 28}px`, top: `${box.y + 6}px` };
}

function measureBoard(): void {
  const el = boardEl.value;
  if (el) boardW.value = el.clientWidth;
  measureNames();
}

function liveLayout(): ProfileLayout {
  const width = Math.max(boardW.value, 1);
  const next = withCoreCards(placed.value ?? defaultProfileLayout(width), width);
  if (placed.value !== next) placed.value = next;
  return next;
}

function hidePanel(id: string): void {
  const next = hideCard(liveLayout(), id);
  placed.value = next;
  writeProfileLayout(next);
}

function addPanel(id: string): void {
  const next = showCard(liveLayout(), id, boardW.value);
  placed.value = next;
  writeProfileLayout(next);
}

function startStatDrag(e: PointerEvent, id: string): void {
  if (!editing.value || e.button !== 0) return;
  if (e.target instanceof Element && e.target.closest('button')) return;
  const width = boardW.value;
  if (width < 1 || !layout.value.cards[id]?.shown) return;
  const handle = e.target instanceof Element && e.target.closest('.resize');
  const current = liveLayout();
  const card = current.cards[id];
  if (!card) return;
  dragSnapshot = JSON.stringify(card);
  statDrag = { id, mode: handle ? 'resize' : 'move', x: e.clientX, y: e.clientY, box: cardPixels(card, width) };
  (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  e.preventDefault();
}

function moveStatDrag(e: PointerEvent): void {
  if (!statDrag || !placed.value) return;
  const width = boardW.value;
  const dx = e.clientX - statDrag.x;
  const dy = e.clientY - statDrag.y;
  let { x, y, w, h } = statDrag.box;
  if (statDrag.mode === 'move') {
    w = Math.min(w, width);
    x = statDrag.box.x + dx;
    y = statDrag.box.y + dy;
  } else {
    x = Math.min(statDrag.box.x, width - STAT_MIN_W);
    w = statDrag.box.w + dx;
    h = statDrag.box.h + dy;
  }
  const others = Object.entries(placed.value.cards)
    .filter(([id, card]) => id !== statDrag!.id && card.shown && knownIds.value.has(id))
    .map(([, card]) => cardPixels(card, width));
  const snapped = snapStatBox({ x, y, w, h }, others, width, statDrag.mode, STAT_MIN_W, STAT_MIN_H, PROFILE_BOARD_MAX_H);
  statGuides.value = snapped.guides;
  placed.value = moveCard(placed.value, statDrag.id, snapped.box, width);
}

function endStatDrag(): void {
  if (!statDrag || !placed.value) {
    statDrag = null;
    statGuides.value = [];
    return;
  }
  const id = statDrag.id;
  statDrag = null;
  statGuides.value = [];
  if (JSON.stringify(placed.value.cards[id]) === dragSnapshot) return;
  placed.value = markLayoutPending(placed.value);
  writeProfileLayout(placed.value);
}

function openPopular(): void {
  if (editing.value || !popular.value) return;
  app.openCanvas(popular.value.id);
}

/** API 4.10: cards a module made with `show` go on the tray (once the screen knows them). */
function takeShowRequests(): void {
  for (const id of [...showRequests]) {
    if (!profilePanels().some((p) => p.id === id)) continue;
    showRequests.splice(showRequests.indexOf(id), 1);
    if (!layout.value.cards[id]?.shown) addPanel(id);
  }
}
watch(() => [showRequests.length, modulesState.rev], () => nextTick(takeShowRequests));

onMounted(() => {
  measureBoard();
  void nextTick(takeShowRequests);
  statObserver = new ResizeObserver(measureBoard);
  if (boardEl.value) statObserver.observe(boardEl.value);
  const face = namesEl.value?.querySelector('.face');
  if (face) statObserver.observe(face);
});

/** Opened most often; a canvas never opened yet falls back to the one with the most objects, then the latest. */
const popular = computed<CanvasMetaExt | null>(() => {
  const list = app.canvases;
  if (!list.length) return null;
  const opens = canvasOpens();
  return [...list].sort((a, b) => (opens[b.id] ?? 0) - (opens[a.id] ?? 0) || b.objectCount - a.objectCount || b.updatedAt - a.updatedAt)[0];
});
</script>

<template>
  <div class="profile">
    <AppNav />
    <main class="page">
      <div class="page-inner layout">
        <div class="column">
          <div class="head">
          <h1>{{ t('profile.title') }}</h1>
          <div class="head-actions">
            <button class="pill settings" @click="app.go('settings')"><Icon name="settings" :size="15" />{{ t('app.settings') }}</button>
            <div class="edit-wrap">
              <button type="button" class="edit-launch" :class="{ active: editing }" :aria-pressed="editing" :aria-expanded="editing" aria-controls="profile-library" :aria-label="t('profile.editStats')" :title="t('profile.editStats')" data-act="profile-edit" @click="editing = !editing"><Icon :name="editing ? 'check' : 'pen'" :size="16" /></button>
            </div>
            <button type="button" class="feed-launch" :class="{ active: ui.chatOpen }" :aria-pressed="ui.chatOpen" :aria-expanded="ui.chatOpen" :aria-label="t('feed.open')" :title="t('feed.open')" data-act="feed-profile" @click="ui.chatOpen = !ui.chatOpen"><Icon name="menu" :size="16" /></button>
          </div>
        </div>
        <div v-if="editing" id="profile-library" ref="libEl" class="library float card" role="dialog" :aria-label="t('profile.library')" :style="libPos ? { left: libPos.x + 'px', top: libPos.y + 'px', right: 'auto' } : undefined" @pointerdown="startLibDrag" @pointermove="moveLibDrag" @pointerup="endLibDrag" @pointercancel="endLibDrag">
          <header class="lib-head">
            <Icon name="tiles" :size="16" />
            <strong>{{ t('profile.library') }}</strong>
            <button type="button" class="icon-btn" :aria-label="t('look.done')" :title="t('look.done')" @click="editing = false"><Icon name="close" :size="15" /></button>
          </header>

          <p class="lib-sec">{{ t('profile.panels') }}</p>
          <p v-if="!libraryItems.length" class="lib-empty">{{ t('profile.libraryEmpty') }}</p>
          <div v-for="panel in libraryItems" v-else :key="panel.id" class="lib-item">
            <span class="lib-name">{{ panelTitle(panel) }}</span>
            <button type="button" class="pill small" :data-panel="panel.id" data-act="add-panel" @click="addPanel(panel.id)"><Icon name="plus" :size="13" />{{ t('profile.addPanel') }}</button>
          </div>

          <template v-if="userCards().length">
            <p class="lib-sec">{{ t('cardEditor.mine') }}</p>
            <div v-for="[id, c] in userCards()" :key="id" class="lib-item">
              <span class="lib-name">{{ c.model.title || t('cardEditor.untitled') }}</span>
              <button type="button" class="icon-btn" :data-edit-user-card="id" :aria-label="t('profile.editCard')" :title="t('profile.editCard')" @click="openCardEditor(id)"><Icon name="pen" :size="14" /></button>
              <button type="button" class="icon-btn" :data-remove-user-card="id" :aria-label="t('cardEditor.remove')" :title="t('cardEditor.remove')" @click="deleteUserCard(id)"><Icon name="trash" :size="14" /></button>
            </div>
          </template>

          <template v-if="cardMakers.length">
            <p class="lib-sec">{{ t('profile.cardMakers') }}</p>
            <div v-for="f in cardMakers" :key="f.moduleId + f.command" class="lib-item">
              <span class="lib-name">{{ f.label }}</span>
              <button type="button" class="pill small" :data-maker="f.moduleId" @click="runCommand(f.moduleId, f.command, [])"><Icon name="module" :size="13" />{{ t('profile.openMaker') }}</button>
            </div>
          </template>

          <p class="lib-sec">{{ stickersFull ? t('profile.stickersFull') : t('profile.stickers') }}</p>
          <div class="picker-grid" :title="t('profile.stickersHint')">
            <button v-for="e in STICKER_SET" :key="e" type="button" class="emoji" :data-emoji="e" :disabled="stickersFull" @click="stick(e)">{{ e }}</button>
          </div>
        </div>
        <div class="identity">
          <span class="avatar" aria-hidden="true">
            <img v-if="profileCard.avatar" :src="profileCard.avatar" alt="" />
            <Icon v-else name="user" :size="28" />
          </span>
          <div class="names-tilt" @pointermove="onNameTilt" @pointerleave="endNameTilt" @pointercancel="endNameTilt">
            <section ref="namesEl" class="names" :aria-label="t('profile.title')">
              <span v-for="n in 5" :key="n" class="ply" :style="{ '--z': n }" aria-hidden="true"></span>
              <div class="face">
              <dl>
                <div>
                  <dt>{{ t('profile.firstName') }}</dt>
                  <dd>{{ profileCard.firstName.trim() || t('profile.unset') }}</dd>
                </div>
                <div>
                  <dt>{{ t('profile.lastName') }}</dt>
                  <dd>{{ profileCard.lastName.trim() || t('profile.unset') }}</dd>
                </div>
              </dl>
              <span
                v-for="s in stickers.items"
                :key="s.id"
                class="sticker"
                :class="{ editing }"
                :data-sticker="s.id"
                :style="stickerStyle(s)"
                aria-hidden="true"
                @pointerdown="startSticker($event, s.id)"
                @pointermove="dragSticker"
                @pointerup="endSticker"
                @pointercancel="endSticker"
                ><span class="glyph">{{ s.e }}</span><button v-if="editing" type="button" class="peel" :aria-label="t('profile.removeSticker')" data-act="peel-sticker" @click.stop="peel(s.id)"><Icon name="close" :size="9" /></button></span
              >
              </div>
            </section>
          </div>
        </div>
        <div class="stats" :class="{ editing }" :style="frameCss(trayLook)" data-tray>
          <CardBackdrop :look="trayLook" base="color-mix(in srgb, var(--text) 8%, var(--bg))" />
          <div ref="boardEl" class="stats-board" :style="{ height: boardH + 'px' }">
            <section v-if="isShown('canvases')" data-stat="canvases" class="stat" :style="statStyle('canvases')" @pointerdown="startStatDrag($event, 'canvases')" @pointermove="moveStatDrag" @pointerup="endStatDrag" @pointercancel="endStatDrag">
              <CardBackdrop :look="lookOf('canvases')" base="var(--panel)" />
              <span class="num">{{ canvasCount }}</span>
              <span class="lbl">{{ t('profile.canvases') }}</span>
              <span v-if="editing" class="resize" aria-hidden="true"></span>
            </section>
            <section v-if="isShown('objects')" data-stat="objects" class="stat" :style="statStyle('objects')" @pointerdown="startStatDrag($event, 'objects')" @pointermove="moveStatDrag" @pointerup="endStatDrag" @pointercancel="endStatDrag">
              <CardBackdrop :look="lookOf('objects')" base="var(--panel)" />
              <span class="num">{{ objectCount }}</span>
              <span class="lbl">{{ t('profile.objects') }}</span>
              <span v-if="editing" class="resize" aria-hidden="true"></span>
            </section>
            <section v-if="isShown('popular')" data-stat="popular" class="stat popular" :style="statStyle('popular')" @pointerdown="startStatDrag($event, 'popular')" @pointermove="moveStatDrag" @pointerup="endStatDrag" @pointercancel="endStatDrag">
              <CardBackdrop :look="lookOf('popular')" base="var(--panel)" />
              <span class="lbl">{{ t('profile.popular') }}</span>
              <template v-if="popular">
                <button class="name" @click="openPopular">{{ popular.name }}</button>
                <span class="sub">{{ tn('home.objects', popular.objectCount) }}</span>
              </template>
              <p v-else-if="app.canvasesLoaded" class="empty">{{ t('profile.popularEmpty') }}</p>
              <span v-if="editing" class="resize" aria-hidden="true"></span>
            </section>
            <section v-if="isShown('clock')" data-stat="clock" class="stat clock" :style="statStyle('clock')" @pointerdown="startStatDrag($event, 'clock')" @pointermove="moveStatDrag" @pointerup="endStatDrag" @pointercancel="endStatDrag">
              <CardBackdrop :look="lookOf('clock')" base="var(--panel)" />
              <time class="num" :datetime="clockStamp">{{ clockTime }}</time>
              <span class="lbl">{{ clockDate }}</span>
              <span v-if="editing" class="resize" aria-hidden="true"></span>
            </section>
            <section v-for="panel in extraShown" :key="panel.id" class="stat" :class="{ module: panel.origin === 'module' }" :data-stat="panel.id" :style="statStyle(panel.id)" @pointerdown="startStatDrag($event, panel.id)" @pointermove="moveStatDrag" @pointerup="endStatDrag" @pointercancel="endStatDrag">
              <CardBackdrop :look="lookOf(panel.id)" base="var(--panel)" />
              <!-- a card with a background of its own is designed whole: the platform's caption would repeat it -->
              <span v-if="!lookOf(panel.id).background" class="lbl">{{ panelTitle(panel) }}</span>
              <ProfileCardFrame
                v-if="panel.origin === 'module' && panel.moduleId && panel.card"
                :html="profileCardHtml(panel.moduleId, panel.card) ?? ''"
                :panel-id="panel.id"
                :module-id="panel.moduleId"
                :card="panel.card"
                :title="panelTitle(panel)"
                :editing="editing"
                :uses="panel.uses ?? []"
                :stats="cardStats"
              />
              <ProfileCardFrame v-else-if="panel.origin === 'user' && panel.html" :html="panel.html" :panel-id="panel.id" :title="panelTitle(panel)" :editing="editing" :uses="panel.uses ?? []" :stats="cardStats" />
              <span v-if="editing" class="resize" aria-hidden="true"></span>
            </section>
            <button v-for="id in shownIds" v-show="editing" :key="id" type="button" class="remove" :style="removeStyle(id)" :data-card="id" :aria-label="t('profile.removePanel')" @pointerdown.stop @click.stop="hidePanel(id)"><Icon name="close" :size="12" /></button>
            <button v-for="id in shownIds" v-show="editing" :key="'look-' + id" type="button" class="remove" :style="lookBtnStyle(id)" :data-look-card="id" :aria-label="t('look.edit')" :title="t('look.edit')" @pointerdown.stop @click.stop="lookFor = id"><Icon name="palette" :size="12" /></button>
            <button v-for="id in shownIds.filter((x) => x.startsWith('user:'))" v-show="editing" :key="'edit-' + id" type="button" class="remove" :style="editBtnStyle(id)" :data-edit-card="id" :aria-label="t('profile.editCard')" :title="t('profile.editCard')" @pointerdown.stop @click.stop="openCardEditor(id.slice(5))"><Icon name="pen" :size="12" /></button>
            <span v-for="(g, i) in statGuides" :key="i" class="guide" :class="g.axis" :style="g.axis === 'x' ? { left: g.at + 'px', top: g.from + 'px', height: g.to - g.from + 'px' } : { top: g.at + 'px', left: g.from + 'px', width: g.to - g.from + 'px' }"></span>
          </div>
        </div>
        </div>
      </div>
    </main>
    <LookEditor v-if="editing && lookFor" :look="editedLook()" :title="lookTitle" @change="changeLook" @reset="resetLook" @close="lookFor = null" />
  </div>
</template>

<style scoped>
.profile {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--bg);
}
.page {
  flex: 1;
  min-height: 0;
  height: auto;
}
.layout {
  max-width: none;
  width: 100%;
  margin: 0;
  padding: 16px clamp(16px, 2.6vw, 44px) 48px;
}
.column {
  width: 100%;
}
.head {
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}
h1 {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
}
.identity {
  display: flex;
  align-items: center;
  gap: 18px;
  margin-bottom: 16px;
}
.avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--border);
  background: var(--panel);
  color: var(--text-2);
  box-shadow: var(--shadow);
  flex: none;
  overflow: hidden;
}
.names-tilt {
  flex: 1;
  min-width: 0;
  perspective: 520px;
}
.names {
  position: relative;
  width: 100%;
  transform-style: preserve-3d;
  transform: rotateX(calc(var(--rx, 0) * 1deg)) rotateY(calc(var(--ry, 0) * 1deg));
}
.face {
  position: relative;
  z-index: 2;
  padding: 14px 18px;
  border: 1px solid color-mix(in srgb, var(--on-accent) 28%, transparent);
  border-radius: var(--radius-panel);
  background: var(--accent);
  color: var(--on-accent);
  transform: translateZ(1px);
  overflow: hidden;
  box-shadow:
    var(--shadow),
    calc(var(--ry, 0) * -1.4px) calc(var(--rx, 0) * 1.1px) 18px color-mix(in srgb, var(--text) 12%, transparent);
}
/* Thin plates share the face outline, so the rim stays rounded. Hidden until the card tilts. */
.ply {
  position: absolute;
  inset: 0;
  border-radius: var(--radius-panel);
  background: color-mix(in srgb, var(--accent) 74%, #000);
  opacity: var(--edge, 0);
  pointer-events: none;
  transform: translateZ(calc((var(--z) - 1) * -1px));
}
/* Specular rim: only the border catches the light, brightest nearest the pointer. */
.face::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  padding: 1.5px;
  background:
    radial-gradient(
      200px 90px at var(--gx, 50%) var(--gy, 50%),
      white,
      color-mix(in srgb, var(--accent) 82%, white) 36%,
      transparent 72%
    ),
    radial-gradient(
      200px 90px at calc(100% - var(--gx, 50%)) calc(100% - var(--gy, 50%)),
      color-mix(in srgb, var(--text) 45%, transparent),
      transparent 70%
    );
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  opacity: 0;
  z-index: 2;
  transition: opacity 0.45s ease;
}
.names.lit .face::before {
  opacity: 1;
}
.names dt {
  color: color-mix(in srgb, var(--on-accent) 72%, transparent);
}
.names dd {
  color: var(--on-accent);
}
.sticker {
  position: absolute;
  z-index: 3;
  font-size: 32px;
  line-height: 1;
  transform: translate(-50%, -50%) rotate(var(--r, 0deg));
  filter: drop-shadow(0 1px 1px color-mix(in srgb, #000 28%, transparent));
  pointer-events: none;
  user-select: none;
}
.glyph {
  display: block;
  transform-origin: var(--ox, 50%) var(--oy, 50%);
  transform: rotateX(calc(var(--fy, 0) * 86deg)) rotateY(calc(var(--fx, 0) * 86deg));
}
.sticker.editing {
  pointer-events: auto;
  cursor: grab;
  touch-action: none;
}
.sticker.editing:active {
  cursor: grabbing;
}
.peel {
  position: absolute;
  top: -8px;
  right: -10px;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: var(--panel);
  color: var(--text-2);
  box-shadow: var(--shadow);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.names dl {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 28px;
  margin: 0;
}
dt {
  font-size: 12px;
  color: var(--text-3);
}
dd {
  margin: 2px 0 0;
  font-size: 16px;
  font-weight: 600;
}
.stats {
  width: 100%;
  max-height: 509px;
  margin-bottom: 22px;
  padding: 12px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  background: color-mix(in srgb, var(--text) 8%, var(--bg));
}
.stats {
  position: relative;
}
.stats-board {
  position: relative;
  z-index: 1;
}
/* the look's backdrop lies under the card's content (module API 4.10) */
.stat > :not(.backdrop):not(.resize) {
  position: relative;
  z-index: 1;
}
.resize {
  z-index: 2;
}
.makers {
  margin-top: 10px;
}
.stat {
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  min-height: 0;
  padding: 16px 18px;
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  background: var(--panel);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.stats.editing .stat {
  cursor: grab;
  touch-action: none;
  user-select: none;
  outline: 1px dashed color-mix(in srgb, var(--accent) 55%, var(--border));
}
.stats.editing .stat:active {
  cursor: grabbing;
  z-index: 2;
}
.stats.editing .name {
  pointer-events: none;
}
.remove {
  position: absolute;
  z-index: 5;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: color-mix(in srgb, var(--text) 8%, var(--panel));
  color: var(--text-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.remove:hover {
  color: var(--text);
}
.edit-wrap {
  position: relative;
}
.library {
  position: fixed;
  z-index: 30;
  top: calc(72px + var(--banner-h, 0px));
  right: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: min(340px, calc(100vw - 32px));
  max-height: calc(100vh - 92px - var(--banner-h, 0px));
  overflow: auto;
  padding: 12px 14px 14px;
  cursor: grab;
  touch-action: none;
}
.library:active {
  cursor: grabbing;
}
.lib-head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-2);
}
.lib-head strong {
  flex: 1;
  color: var(--text);
  font-size: 13.5px;
}
.lib-sec {
  margin: 8px 0 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-3);
}
.lib-empty {
  margin: 0;
  color: var(--text-3);
  font-size: 12.5px;
}
.lib-item {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  padding: 2px 4px 2px 10px;
  border-radius: 12px;
  font-size: 13px;
}
.lib-item:hover {
  background: color-mix(in srgb, var(--text) 5%, transparent);
}
.lib-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.library .pill.small {
  align-self: flex-start;
  font-size: 12.5px;
}
.lib-item .pill.small {
  align-self: center;
}
.picker-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
}
.emoji {
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: none;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
}
.emoji:hover:not(:disabled) {
  background: color-mix(in srgb, var(--text) 8%, transparent);
}
.emoji:disabled {
  opacity: 0.4;
  cursor: default;
}
.resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 22px;
  height: 22px;
  cursor: nwse-resize;
}
.resize::before {
  content: '';
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 10px;
  height: 10px;
  border-right: 2px solid var(--text-3);
  border-bottom: 2px solid var(--text-3);
}
.guide {
  position: absolute;
  z-index: 3;
  pointer-events: none;
}
.guide.x {
  width: 0;
  border-left: 1px dashed var(--accent);
}
.guide.y {
  height: 0;
  border-top: 1px dashed var(--accent);
}
.num {
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
  line-height: 1.1;
}
.clock .num {
  font-size: 26px;
  white-space: nowrap;
}
.lbl {
  color: var(--text-3);
  font-size: 13px;
}
.stat.module {
  justify-content: flex-start;
  padding: 12px 14px 8px;
}
.popular .lbl {
  margin-bottom: 4px;
}
.name {
  max-width: 100%;
  padding: 0;
  border: none;
  background: none;
  color: var(--text);
  font: inherit;
  font-size: 18px;
  font-weight: 600;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
.name:hover {
  color: var(--accent-text);
}
.sub,
.empty {
  margin: 0;
  color: var(--text-2);
  font-size: 13px;
}
.head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.settings {
  height: 36px;
  padding: 0 16px;
  gap: 8px;
  font-size: 13px;
}
.feed-launch,
.edit-launch {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--panel);
  color: var(--text-2);
  box-shadow: var(--shadow);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  cursor: pointer;
}
.feed-launch:hover,
.edit-launch:hover,
.feed-launch.active,
.edit-launch.active {
  color: var(--text);
}
.feed-launch.active,
.edit-launch.active {
  box-shadow: var(--shadow), 0 0 0 2px var(--accent);
}
@media (prefers-reduced-motion: reduce) {
  .names {
    transform: none;
  }
  .ply {
    opacity: 0;
  }
  .face::before {
    opacity: 0;
  }
}
</style>
